import React, { useEffect, useMemo, useState } from "react";
import {
  FaCloudUploadAlt,
  FaCog,
  FaDownload,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaFileAlt,
  FaGlobeAmericas,
  FaPlus,
  FaSearch,
  FaStore,
  FaUserTie,
  FaWallet,
} from "react-icons/fa";
import "../styles/ReportingPage.css";
import AppDropdown from "../components/AppDropdown";
import * as XLSX from "xlsx";
import { FaCheck } from "react-icons/fa";
import {
  getReportSummary,
  getReportVolumeTrends,
  getReportRecentActivity,
  generateMerchantReport,
  generateAgentReport,
  generateTransactionReport,
  getPortalMerchantsList,
  listOrgAgents,
} from "../api/api";
import { getOrgId, getUserData } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";
import useSidebarAction from "../hooks/useSidebarAction";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const pick = (res) => res?.data ?? res ?? null;
const asArray = (raw) =>
  (Array.isArray(raw) && raw) ||
  (Array.isArray(raw?.data) && raw.data) ||
  (Array.isArray(raw?.categories) && raw.categories) ||
  (Array.isArray(raw?.items) && raw.items) ||
  (Array.isArray(raw?.reports) && raw.reports) ||
  (Array.isArray(raw?.activities) && raw.activities) ||
  (Array.isArray(raw?.activity) && raw.activity) ||
  (Array.isArray(raw?.rows) && raw.rows) ||
  (Array.isArray(raw?.list) && raw.list) ||
  (Array.isArray(raw?.data?.activities) && raw.data.activities) ||
  (Array.isArray(raw?.data?.reports) && raw.data.reports) ||
  (Array.isArray(raw?.data?.rows) && raw.data.rows) ||
  (Array.isArray(raw?.data?.list) && raw.data.list) ||
  [];

const DEFAULT_CATEGORIES = [
  { key: "merchant", title: "Merchant Reports", subtitle: "Settlement reconciliations & fee audits", icon: <FaStore /> },
  { key: "agent", title: "Agent Reports", subtitle: "Commission tracking & performance", icon: <FaUserTie /> },
  { key: "transaction", title: "Transactions", subtitle: "Full transaction history & raw data", icon: <FaExchangeAlt /> },
  { key: "settlement", title: "Settlements", subtitle: "Payout cycles & bank transfers", icon: <FaWallet /> },
];

const generatorFor = (key) => {
  if (key.includes("agent")) return generateAgentReport;
  if (key.includes("transaction")) return generateTransactionReport;
  if (key.includes("settle")) return generateTransactionReport;
  return generateMerchantReport;
};

/* Local activity log — the recent-activity API currently returns a PDF
   instead of JSON (backend bug), so reports generated from this browser
   are recorded locally and merged into the table until that's fixed */
const activityStorageKey = (orgId) => `reportActivity:${orgId}`;
const readLocalActivity = (orgId) => {
  try {
    const list = JSON.parse(localStorage.getItem(activityStorageKey(orgId)));
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};
const logLocalActivity = (orgId, entry) => {
  try {
    const list = [entry, ...readLocalActivity(orgId)].slice(0, 20);
    localStorage.setItem(activityStorageKey(orgId), JSON.stringify(list));
  } catch {
    /* storage full/blocked — non-fatal */
  }
};
const nowLabel = () =>
  new Date().toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

/* Last-run timestamps recorded locally per org when a report is generated
   (the summary API doesn't provide them) */
const lastRunStorageKey = (orgId) => `reportLastRun:${orgId}`;
const readLastRuns = (orgId) => {
  try {
    return JSON.parse(localStorage.getItem(lastRunStorageKey(orgId))) || {};
  } catch {
    return {};
  }
};
const recordLastRun = (orgId, typeKey) => {
  const map = readLastRuns(orgId);
  map[typeKey] = new Date().toISOString();
  try {
    localStorage.setItem(lastRunStorageKey(orgId), JSON.stringify(map));
  } catch {
    /* storage full/blocked — non-fatal */
  }
};

/* Row builders shared by Generate Report and the Recent Activity download */
const buildMerchantRows = async (orgId) => {
  const res = await getPortalMerchantsList({ orgId, page: 1, limit: 1000 });
  const payload = pick(res) ?? {};
  const list =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.merchants) && payload.merchants) ||
    (Array.isArray(payload?.data) && payload.data) ||
    [];
  return list.map((m) => ({
    "Merchant ID": m.merchantID || m.MID || "",
    "Merchant Name": m.merchantName || m.name || "",
    "Category": m.categoryName || m.category || "",
    "Region": m.region || m.country || "",
    "Status": m.status || "",
    "Assigned Agent": m.assignedAgent || m.agentName || m.agent || "",
    "Static QRs": Number(m.staticQr ?? m.staticQrCount ?? m.staticCount ?? 0),
    "Dynamic QRs": Number(m.dynamicQr ?? m.dynamicQrCount ?? m.dynamicCount ?? 0),
    "Volume (Rs)": Number(m.volume ?? m.totalVolume ?? m.monthlyVolume ?? 0),
    "Last Activity": m.lastActivity || m.lastTxnAt || m.updatedAt || "",
  }));
};

const buildAgentRows = async (orgId) => {
  const res = await listOrgAgents({ orgId, page: 1, limit: 1000, dateRange: "last30days", status: "all" });
  const payload = pick(res) ?? {};
  const list =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.agents) && payload.agents) ||
    (Array.isArray(payload?.data) && payload.data) ||
    [];
  return list.map((a) => ({
    "Agent Name": a.name || a.agentName || "",
    "Code": a.code || (a.cnic ? `AG-${String(a.cnic).slice(-6)}` : ""),
    "CNIC": a.cnic || "",
    "Email": a.email || "",
    "Phone": a.phoneNumber || a.phone || "",
    "Region": a.region || "",
    "Status": a.status || "",
    "Merchants": Number(a.merchants ?? a.totalMerchants ?? a.merchantCount ?? 0),
    "Volume (Rs)": Number(a.volume ?? a.totalVolume ?? a.monthlyVolume ?? 0),
    "Commission (Rs)": Number(a.commission ?? a.totalCommission ?? a.commissionEarned ?? 0),
    "Pending Payout (Rs)": Number(a.payout ?? a.duePayout ?? a.pendingPayout ?? 0),
    "Last Active": a.lastActive || a.lastActiveAt || a.updatedAt || "",
  }));
};

const downloadXlsx = (rows, fileName, sheetName = "Report") => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName.replace(/\.(pdf|csv)$/i, ".xlsx"));
};

export default function Reporting({ isSidebarOpen }) {
  const { showSuccess, showError } = useToast();
  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = getOrgId() || user?.profile?.orgId || user?.orgId || user?.organizationId || null;

  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [apiCategories, setApiCategories] = useState([]);
  const [volumeBars, setVolumeBars] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [volumeMeta, setVolumeMeta] = useState({ label: "Last 7 Days", change: "0%" });
  const [recentRows, setRecentRows] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Export modal
  const [exportOpen, setExportOpen] = useState(false);

  /* Sidebar dropdown action → open the same "New Report" export modal */
  useSidebarAction({ new: () => setExportOpen(true) });

  const [expType, setExpType] = useState("transaction");
  const [expMerchant, setExpMerchant] = useState("all");
  const [expRange, setExpRange] = useState("last30days");
  const [expFormat, setExpFormat] = useState("csv");
  const [expRecurring, setExpRecurring] = useState(false);
  const [expBusy, setExpBusy] = useState(false);
  const [expDone, setExpDone] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    const run = async () => {
      const results = await Promise.allSettled([
        getReportSummary(orgId),
        getReportVolumeTrends(orgId),
        getReportRecentActivity(orgId, 1, 10),
      ]);
      if (cancelled) return;
      const val = (i) => (results[i].status === "fulfilled" ? pick(results[i].value) : null);

      const sum = val(0) || {};
      setSummary(sum);
      setApiCategories(asArray(sum.categories ?? sum));

      // Volume trends
      const trendPayload = results[1].status === "fulfilled" ? (results[1].value || {}) : {};
      const trendArr = asArray(trendPayload?.data ?? trendPayload).map((d) =>
        typeof d === "number" ? d : num(d?.value ?? d?.amount ?? d?.volume ?? d?.total)
      );
      if (trendArr.length) {
        const maxT = Math.max(...trendArr, 0);
        setVolumeBars(trendArr.map((v) => (maxT ? Math.round((v / maxT) * 100) : 0)));
      } else {
        setVolumeBars([12, 18, 14, 22, 16, 24, 20]);
      }
      const growthVal = trendPayload?.change ?? trendPayload?.growth;
      setVolumeMeta({
        label: trendPayload?.label ?? trendPayload?.range ?? "Last 7 Days",
        change: typeof growthVal === "number" ? `${growthVal}%` : (growthVal ?? "0%"),
      });

      // Recent activity — API rows merged with locally-logged generations
      const apiRows = asArray(val(2)).map((r, idx) => ({
        id: r.id ?? r._id ?? `${r.name ?? "report"}-${idx}`,
        name: r.name ?? r.reportName ?? r.fileName ?? r.title ?? r.report ?? "Report",
        type: r.type ?? r.category ?? r.reportType ?? "—",
        status: String(r.status ?? r.state ?? "Ready"),
        date: r.date ?? r.createdAt ?? r.generatedAt ?? r.time ?? r.when ?? "—",
      }));
      const seenIds = new Set(apiRows.map((r) => r.id));
      const localRows = readLocalActivity(orgId).filter((r) => !seenIds.has(r.id));
      setRecentRows([...localRows, ...apiRows].slice(0, 10));
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [orgId, refreshKey]);

  // Merge live stats with default shells
  const categories = useMemo(() => {
    const localRuns = readLastRuns(orgId);
    return DEFAULT_CATEGORIES.map((cat) => {
      const match = apiCategories.find((c) => {
        const t = String(c?.type ?? c?.key ?? c?.name ?? c?.title ?? "").toLowerCase();
        return t.includes(cat.key);
      });
      const lastRunRaw =
        localRuns[cat.key] ?? match?.lastRun ?? match?.lastGenerated ?? match?.lastRunAt ?? null;
      return {
        ...cat,
        lastRun: lastRunRaw ? fmtDate(lastRunRaw) : "—",
        avgSize: match?.avgSize ?? match?.averageSize ?? "0 MB",
      };
    });
  }, [apiCategories, orgId, refreshKey]);

  const metrics = [
    { title: "Total Exports", value: String(num(summary?.totalExports ?? summary?.exports ?? summary?.totalReports)), icon: <FaFileAlt />, meta: "Files generated" },
    { title: "Scheduled", value: String(num(summary?.scheduled ?? summary?.scheduledJobs ?? summary?.automations)), icon: <FaCog />, meta: "Automations active" },
    {
      title: "Storage Used",
      value: `${num(summary?.storageUsed)}GB/${num(summary?.storageLimit ?? 5)}GB`,
      icon: <FaCloudUploadAlt />,
      meta: `${num(summary?.utilization ?? summary?.storagePercent)}% utilization`,
      progress: num(summary?.utilization ?? summary?.storagePercent),
    },
  ];

  const filteredRows = useMemo(
    () =>
      recentRows.filter((row) =>
        `${row.name} ${row.type} ${row.status}`.toLowerCase().includes(search.toLowerCase())
      ),
    [recentRows, search]
  );

  const handleGenerate = async (cat) => {
    if (!orgId) {
      showError("Error", "No organization selected", 3000);
      return;
    }
    if (busyKey) return;
    try {
      setBusyKey(cat.key);

      /* Merchant report: built from the live portal-merchants-list data */
      if (cat.key === "merchant") {
        const rows = await buildMerchantRows(orgId);
        if (!rows.length) {
          showError("Report", "No merchants found to include in the report", 3000);
          return;
        }
        const fileName = `merchant-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
        downloadXlsx(rows, fileName, "Merchants");
        showSuccess("Report", `${cat.title} generated (${rows.length} merchants)`);
        recordLastRun(orgId, cat.key);
        logLocalActivity(orgId, {
          id: `local-${Date.now()}`,
          name: fileName,
          type: cat.key,
          status: "Ready",
          date: nowLabel(),
        });
        setRefreshKey((k) => k + 1);
        return;
      }

      /* Agent report: built from the live agents/list data */
      if (cat.key === "agent") {
        const rows = await buildAgentRows(orgId);
        if (!rows.length) {
          showError("Report", "No agents found to include in the report", 3000);
          return;
        }
        const fileName = `agent-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
        downloadXlsx(rows, fileName, "Agents");
        showSuccess("Report", `${cat.title} generated (${rows.length} agents)`);
        recordLastRun(orgId, cat.key);
        logLocalActivity(orgId, {
          id: `local-${Date.now()}`,
          name: fileName,
          type: cat.key,
          status: "Ready",
          date: nowLabel(),
        });
        setRefreshKey((k) => k + 1);
        return;
      }

      const res = await generatorFor(cat.key)(orgId);
      const url = res?.data?.url ?? res?.url ?? res?.data?.downloadUrl ?? res?.downloadUrl;
      showSuccess("Report", res?.message || `${cat.title} generated`);
      if (url) window.open(url, "_blank", "noopener");
      recordLastRun(orgId, cat.key);
      logLocalActivity(orgId, {
        id: `local-${Date.now()}`,
        name: cat.title,
        type: cat.key,
        status: "Ready",
        date: nowLabel(),
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Failed to generate report", 3000);
    } finally {
      setBusyKey(null);
    }
  };

  const handleConfigureGenerate = async () => {
    if (!orgId) {
      showError("Error", "No organization selected", 3000);
      return;
    }
    if (expBusy) return;
    try {
      setExpBusy(true);
      const res = await generatorFor(expType)(orgId);
      const payload = pick(res) || {};
      const url = payload?.url ?? payload?.downloadUrl ?? res?.url ?? res?.downloadUrl;
      const rows = asArray(payload?.rows ?? payload?.records ?? payload?.report ?? payload);
      const ext = expFormat === "xlsx" ? "xlsx" : expFormat === "pdf" ? "pdf" : "csv";
      /* the API names this `reportName`; fall back to a locally built name */
      const serverName = payload?.fileName ?? payload?.reportName ?? res?.fileName;
      const baseName =
        serverName || `${expType}-report-${new Date().toISOString().slice(0, 10)}`;
      const fileName = /\.(xlsx|csv|pdf)$/i.test(baseName)
        ? baseName
        : `${baseName}.${ext}`;

      /* Nothing to hand back: no file URL and no rows. Say so instead of
         showing an "Export Complete" card that fails on click. */
      if (!url && !rows.length) {
        setExpDone({ url: null, rows: [], fileName, empty: true });
        return;
      }

      showSuccess("Report", res?.message || "Report generated");
      setExpDone({ url, rows, fileName, empty: false });
      recordLastRun(orgId, expType === "settlement" ? "settlement" : expType);
      logLocalActivity(orgId, {
        id: `local-${Date.now()}`,
        name: fileName,
        type: expType,
        status: "Ready",
        date: nowLabel(),
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Failed to generate report", 3000);
    } finally {
      setExpBusy(false);
    }
  };

  /* Recent Activity: re-pull the data for that report type and download the file */
  const [downloadingId, setDownloadingId] = useState(null);
  const handleActivityDownload = async (row) => {
    if (!orgId || downloadingId) return;
    const t = String(row.type).toLowerCase();
    try {
      setDownloadingId(row.id);
      let rows = [];
      let sheet = "Report";
      if (t.includes("merchant")) {
        rows = await buildMerchantRows(orgId);
        sheet = "Merchants";
      } else if (t.includes("agent")) {
        rows = await buildAgentRows(orgId);
        sheet = "Agents";
      } else {
        const res = await generatorFor(t)(orgId);
        const payload = pick(res) || {};
        const url = payload?.url ?? payload?.downloadUrl ?? res?.url ?? res?.downloadUrl;
        if (url) {
          window.open(url, "_blank", "noopener");
          setDownloadingId(null);
          return;
        }
        rows = asArray(payload?.rows ?? payload?.records ?? payload?.report ?? payload);
      }
      if (!rows.length) {
        showError("Download", "No data available for this report", 3000);
        return;
      }
      const fileName = /\.(xlsx|csv|pdf)$/i.test(row.name)
        ? row.name
        : `${row.name}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadXlsx(rows, fileName, sheet);
      showSuccess("Download", `${fileName.replace(/\.(pdf|csv)$/i, ".xlsx")} downloaded (${rows.length} rows)`);
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Failed to download report", 3000);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadReport = () => {
    if (!expDone) return;
    if (expDone.url) {
      const a = document.createElement("a");
      a.href = expDone.url;
      a.download = expDone.fileName;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    if (expDone.rows.length) {
      const ws = XLSX.utils.json_to_sheet(expDone.rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      const localName = expDone.fileName.replace(/\.pdf$/i, ".xlsx");
      XLSX.writeFile(wb, localName);
      return;
    }
    showError("Download", "No downloadable file was returned by the server", 3000);
  };

  const closeExportModal = () => {
    setExportOpen(false);
    setExpDone(null);
  };

  const expRecords = num(summary?.totalRecords ?? summary?.records ?? summary?.totalTransactions);

  return (
    <div className={`rp-page${isSidebarOpen ? " shifted" : ""}`}>
      <div className="rp-topbar">
        <div className="rp-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search reports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rp-metrics">
        {metrics.map((item) => (
          <article className="rp-card rp-metric" key={item.title}>
            <div className="rp-metric__icon">{item.icon}</div>
            <div>
              <small>{item.title}</small>
              <strong>{item.value}</strong>
              {item.progress ? (
                <div className="rp-progress">
                  <span style={{ width: `${item.progress}%` }} />
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <div className="rp-grid">
        <section className="rp-main">
          <div className="rp-section-head">
            <h3>Report Categories</h3>
          </div>

          <div className="rp-categories">
            {categories.map((card) => (
              <article key={card.title} className="rp-card rp-category">
                <div className="rp-category__head">
                  <div>
                    <h4>{card.title}</h4>
                    <p>{card.subtitle}</p>
                  </div>
                  <span>{card.icon}</span>
                </div>
                <div className="rp-category__stats">
                  <div>
                    <small>Last Run</small>
                    <strong>{card.lastRun}</strong>
                  </div>
                  <div>
                    <small>Avg Size</small>
                    <strong>{card.avgSize}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="rp-generate"
                  disabled={busyKey === card.key}
                  onClick={() => handleGenerate(card)}
                >
                  {busyKey === card.key ? "Generating…" : "Generate Report"}
                </button>
              </article>
            ))}
          </div>

          <article className="rp-card rp-activity">
            <div className="rp-activity__head">
              <h3>Recent Activity</h3>
              <button type="button">View all</button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Report Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 24, opacity: 0.6 }}>
                      No reports found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td>{row.type}</td>
                      <td>
                        <span className={`rp-badge rp-badge--${row.status.toLowerCase()}`}>{row.status}</span>
                      </td>
                      <td>{row.date}</td>
                      <td>
                        <button
                          type="button"
                          className="rp-action-btn"
                          title="Download"
                          disabled={downloadingId === row.id}
                          style={downloadingId === row.id ? { opacity: 0.5, cursor: "wait" } : undefined}
                          onClick={() => handleActivityDownload(row)}
                        >
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </article>
        </section>

        <aside className="rp-side">
          <button type="button" className="rp-new-btn" onClick={() => setExportOpen(true)}>
            <FaPlus /> New Report
          </button>

          <article className="rp-card rp-side-card">
            <h4>Volume Trends</h4>
            <div className="rp-trend-bars">
              {volumeBars.map((bar, idx) => (
                <span key={idx} style={{ height: `${bar}%` }} />
              ))}
            </div>
            <div className="rp-trend-meta">
              <small>{volumeMeta.label}</small>
              <strong>{volumeMeta.change}</strong>
            </div>
          </article>

          <article className="rp-card rp-map">
            <FaGlobeAmericas />
            <h5>Live Global Activity</h5>
            <p>Real-time sync active</p>
          </article>
        </aside>
      </div>

      {exportOpen ? (
        <div className="rpx-overlay" role="dialog" aria-modal="true">
          <button type="button" className="rpx-scrim" aria-label="Close" onClick={closeExportModal} />
          {expDone ? (
            expDone.empty ? (
              <div className="rpx-donecard">
                <div className="rpx-donecheck rpx-donecheck--empty"><FaExclamationTriangle /></div>
                <h3>No Data to Export</h3>
                <p>
                  The report was generated but contains no records, so there is
                  nothing to download. Try a different report type or date range.
                </p>
                <button type="button" className="rpx-return" onClick={() => setExpDone(null)}>
                  Back to Configure
                </button>
                <button type="button" className="rpx-return" onClick={closeExportModal}>
                  Close
                </button>
              </div>
            ) : (
            <div className="rpx-donecard">
              <div className="rpx-donecheck"><FaCheck /></div>
              <h3>Export Complete</h3>
              <p>Download Ready: {expDone.fileName}</p>
              <button type="button" className="rpx-download" onClick={handleDownloadReport}>
                Download File
              </button>
              <button type="button" className="rpx-return" onClick={closeExportModal}>
                Return to Dashboard
              </button>
            </div>
            )
          ) : (
          <div className="rpx-modal">
            <div className="rpx-config">
              <div className="rpx-head">
                <div>
                  <h3>Configure Export</h3>
                  <p>Tailor your data export parameters</p>
                </div>
                <button type="button" className="rpx-close" aria-label="Close" onClick={() => setExportOpen(false)}>×</button>
              </div>

              <div className="rpx-sectionlabel"><span>1</span> Report Configuration</div>
              <div className="rpx-fieldrow">
                <div className="rpx-field">
                  <label>Report Type</label>
                  <AppDropdown
                    className="appdd--block"
                    options={[
                      { value: "transaction", label: "Transaction Logs" },
                      { value: "merchant", label: "Merchant Reports" },
                      { value: "agent", label: "Agent Reports" },
                      { value: "settlement", label: "Settlements" },
                    ]}
                    value={expType}
                    onChange={setExpType}
                  />
                </div>
                <div className="rpx-field">
                  <label>Merchants</label>
                  <AppDropdown
                    className="appdd--block"
                    options={[{ value: "all", label: "All Merchants" }]}
                    value={expMerchant}
                    onChange={setExpMerchant}
                  />
                </div>
              </div>
              <div className="rpx-field">
                <label>Time Range</label>
                <AppDropdown
                  className="appdd--block"
                  options={[
                    { value: "today", label: "Today" },
                    { value: "last7days", label: "Last 7 Days" },
                    { value: "last30days", label: "Last 30 Days" },
                    { value: "last90days", label: "Last 90 Days" },
                  ]}
                  value={expRange}
                  onChange={setExpRange}
                />
              </div>

              <div className="rpx-sectionlabel"><span>2</span> Format Selection</div>
              <div className="rpx-formats">
                {["csv", "xlsx", "pdf"].map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={expFormat === f ? "rpx-format active" : "rpx-format"}
                    onClick={() => setExpFormat(f)}
                  >
                    <FaFileAlt />
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>

              <label className="rpx-recurring">
                <span>
                  <strong>Recurring Schedule</strong>
                  <small>Automate this export weekly</small>
                </span>
                <input
                  type="checkbox"
                  checked={expRecurring}
                  onChange={(e) => setExpRecurring(e.target.checked)}
                />
                <i className="rpx-switch" />
              </label>

              <div className="rpx-footer">
                <button type="button" className="rpx-cancel" onClick={() => setExportOpen(false)}>Cancel</button>
                <button type="button" className="rpx-generate" onClick={handleConfigureGenerate} disabled={expBusy}>
                  {expBusy ? "Generating..." : "Generate Report"}
                </button>
              </div>
            </div>

            <div className="rpx-preview">
              <h4>Export Preview</h4>
              <div className="rpx-size">
                <small>Estimated Size</small>
                <strong>{summary?.estimatedSize ?? "—"}</strong>
              </div>
              <div className="rpx-statline"><span>Total Records</span><strong>{expRecords.toLocaleString()}</strong></div>
              <div className="rpx-statline"><span>Columns</span><strong>{num(summary?.columns ?? 24)}</strong></div>
              <div className="rpx-statline"><span>Data Density</span><strong>{summary?.dataDensity ?? "High"}</strong></div>
              <div className="rpx-note">
                Generating high-volume transaction logs may take up to 45 seconds. You will be notified when ready.
              </div>
              <div className="rpx-stream">Live Data Stream</div>
            </div>
          </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
